import { Link } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";

const principles = [
  { icon: "ri-eye-off-line", title: "只围观，不站队", text: "我们不对热议焦点本身下价值判断，而是如实地把不同的声音摆到你面前。争论的是网友，整理的是我们。" },
  { icon: "ri-archive-line", title: "给喧嚣留时间线", text: "每一条热议都被还原成看得见的发酵脉络：从何时冒出、怎样升温、又在何处转向。" },
  { icon: "ri-bar-chart-grouped-line", title: "让观点变结构", text: "散落在评论区的零碎意见，被我们归纳为可读的立场与占比，五分钟看懂大家往哪边倒。" },
  { icon: "ri-shield-check-line", title: "克制转述，注明来源", text: "我们梳理的是「讨论」而非「定论」，内容基于公开讨论归纳，不代表任何事实主张。" },
];

const steps = [
  {
    num: "01",
    title: "捕捉信号",
    text: "从公开讨论声量的异动里，找出那些刚刚开始升温、还来得及「上车围观」的苗头。",
  },
  {
    num: "02",
    title: "拆解发酵",
    text: "沿时间线还原话题的升温路径，理清是哪一类内容、哪一种情绪把讨论推到了高点。",
  },
  {
    num: "03",
    title: "归纳声浪",
    text: "把评论区千奇百怪的发言归类成主要立场，量化占比，让风向一眼可见。",
  },
];

export default function About() {
  usePageMeta(
    "关于我们 - 天涯吃瓜话题发酵追踪与舆论风向观察站",
    "天涯吃瓜是一个专注热议焦点与舆论风向观察的内容站。了解我们的品牌介绍、网站定位、发展理念、内容原则与联系方式。",
    "天涯吃瓜,天涯吃瓜关于我们,天涯吃瓜网站定位,天涯吃瓜内容原则,天涯吃瓜舆论风向"
  );

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "天涯吃瓜",
      description: "专注热议焦点与舆论风向观察的话题发酵追踪站",
      url: "/about",
    },
    {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: "关于天涯吃瓜",
    },
  ];

  return (
    <article>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <section className="relative overflow-hidden bg-gradient-to-br from-secondary-50 via-background-50 to-primary-100/40">
        <div className="mx-auto max-w-4xl px-4 py-14 text-center md:py-20">
          <span className="inline-flex items-center gap-2 rounded-full border border-primary-200 bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700">
            关于天涯吃瓜
          </span>
          <h1 className="mt-5 font-heading text-3xl font-black leading-tight text-foreground-950 md:text-5xl">
            让每一次围观，
            <br />
            都<em className="not-italic text-primary-600">围个明白</em>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-base leading-7 text-foreground-600 md:text-lg">
            天涯吃瓜是一档关于"热议与舆论"的观察栏目。我们追踪话题如何被顶上来、网友观点如何发酵与转向，把乱糟糟的讨论，整理成清楚的时间线与立场结构。
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 py-12 md:px-6">
        {/* 定位 */}
        <section className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
          <div className="overflow-hidden rounded-2xl border border-background-200">
            <img
              src="https://readdy.ai/api/search-image?query=Editorial%20flat%20lay%20of%20newspaper%20clippings%20chat%20bubbles%20and%20a%20magnifying%20glass%20on%20warm%20beige%20background%2C%20concept%20of%20observing%20public%20discussion%20and%20trends%2C%20warm%20coral%20and%20cream%20tones%2C%20minimal%20and%20artistic&width=1200&height=900&seq=about-story&orientation=portrait"
              alt="天涯吃瓜的品牌故事插画"
              title="天涯吃瓜品牌故事 - 观察舆论风向"
              className="h-[380px] w-full object-cover object-top"
            />
          </div>
          <div>
            <span className="text-xs font-semibold tracking-wide text-primary-600">网站定位</span>
            <h2 className="mt-1 font-heading text-2xl font-bold text-foreground-900 md:text-3xl">
              我们不做热点制造者，只做风向观察员
            </h2>
            <p className="mt-4 text-[15px] leading-8 text-foreground-700">
              每当一个话题冲上热议，评论区总是一片各说各话的海洋。有人站队、有人吃瓜、有人被情绪裹挟。天涯吃瓜想做的，是给这片海洋画一张航线图。
            </p>
            <p className="mt-3 text-[15px] leading-8 text-foreground-700">
              我们关注的话题涵盖社会观察、科技圈、职场话题、娱乐现场、民生热议与文化现象。通过追踪话题升温动态、归纳网友观点，我们帮你抓住讨论的热度走势，也帮你理解一次舆论为什么会发酵、又为什么会转向。
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              {["观点中立", "脉络清晰", "更新及时"].map((tag) => (
                <span key={tag} className="rounded-full bg-secondary-100 px-4 py-1.5 text-sm font-medium text-secondary-800">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* 内容原则 */}
        <section className="mt-16">
          <div className="text-center">
            <span className="text-xs font-semibold tracking-wide text-accent-600">内容原则</span>
            <h2 className="mt-1 font-heading text-2xl font-bold text-foreground-900 md:text-3xl">围观，也有原则</h2>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {principles.map((p) => (
              <div key={p.title} className="rounded-2xl border border-background-200 bg-white p-6">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                  <i className={`${p.icon} text-xl`} aria-hidden="true"></i>
                </span>
                <h3 className="mt-4 font-heading text-base font-bold text-foreground-900">{p.title}</h3>
                <p className="mt-2 text-sm leading-6 text-foreground-500">{p.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* 理念三步 */}
        <section className="mt-16 rounded-2xl border border-background-200 bg-background-100 p-6 md:p-10">
          <div className="text-center">
            <span className="text-xs font-semibold tracking-wide text-primary-600">发展理念</span>
            <h2 className="mt-1 font-heading text-2xl font-bold text-foreground-900 md:text-3xl">我们如何追踪一个热议焦点</h2>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-8 md:grid-cols-3">
            {steps.map((s) => (
              <div key={s.num} className="relative">
                <span className="font-heading text-5xl font-black text-primary-200">{s.num}</span>
                <h3 className="mt-3 font-heading text-lg font-bold text-foreground-900">{s.title}</h3>
                <p className="mt-2 text-sm leading-7 text-foreground-600">{s.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* 联系方式 */}
        <section className="mt-16 grid grid-cols-1 items-center gap-8 lg:grid-cols-2">
          <div>
            <span className="text-xs font-semibold tracking-wide text-accent-600">联系我们</span>
            <h2 className="mt-1 font-heading text-2xl font-bold text-foreground-900 md:text-3xl">有话题线索或合作意向？</h2>
            <p className="mt-3 max-w-xl text-[15px] leading-7 text-foreground-600">
              如果你观察到一个正在升温、但还没有进入主流视野的讨论苗头，欢迎告诉我们。也欢迎各类围绕热点与舆论的选题合作。
            </p>
          </div>
          <div className="rounded-2xl border border-background-200 bg-white p-6">
            <ul className="space-y-4 text-sm">
              <li className="flex items-center gap-3 text-foreground-700">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                  <i className="ri-mail-line" aria-hidden="true"></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-400">编辑部邮箱</p>
                  <p className="font-medium">editor@tianya-chigua.example</p>
                </div>
              </li>
              <li className="flex items-center gap-3 text-foreground-700">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                  <i className="ri-lightbulb-line" aria-hidden="true"></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-400">选题 &amp; 线索</p>
                  <p className="font-medium">tip@tianya-chigua.example</p>
                </div>
              </li>
              <li className="flex items-center gap-3 text-foreground-700">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary-100 text-secondary-700">
                  <i className="ri-global-line" aria-hidden="true"></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-400">更新频率</p>
                  <p className="font-medium">全天候持续追踪热议焦点</p>
                </div>
              </li>
            </ul>
          </div>
        </section>
      </div>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-4 pb-16 md:px-6">
        <div className="flex flex-col items-center gap-5 rounded-2xl bg-primary-500 px-6 py-12 text-center text-white">
          <h2 className="font-heading text-2xl font-bold md:text-3xl">从今天开始，围观得更明白一点</h2>
          <p className="max-w-xl text-sm text-primary-100">
            去看看正在发酵的热议焦点，用一条时间线读懂热度，用一组观点结构看懂风向。
          </p>
          <Link
            to="/topics"
            className="inline-flex h-11 items-center gap-2 whitespace-nowrap rounded-full bg-white px-6 text-sm font-semibold text-primary-600 transition-colors hover:bg-background-50"
          >
            围观热议焦点
            <i className="ri-arrow-right-line" aria-hidden="true"></i>
          </Link>
        </div>
      </section>
    </article>
  );
}